import React, { useCallback, useEffect, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Grid,
  Alert,
  PrimaryButtonContained,
} from "../../../../../components";
import {
  getMasterListHeaders,
  mapFilterCriteriaHeader,
} from "../../../../../actions/newRecipientList";
import CriteriaSelector from "./CriteriaSelector";

// Only offer targets the backend will actually accept for this feature - searchable is not
// enough on its own, a header explicitly marked filterable: false must never be selectable here
// (it would otherwise pass this dropdown and only fail later, after a list record already exists).
const getSearchableHeaders = (recipientHeaders = {}) =>
  Object.keys(recipientHeaders)
    .filter(
      (header) =>
        recipientHeaders[header].searchable &&
        recipientHeaders[header].filterable !== false
    )
    .sort()
    .map((name) => ({ ...recipientHeaders[name], name }));

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: "auto",
  },
  description: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
  },
  item: {
    flex: "1 1 0",
    minWidth: 280,
    maxWidth: 600,
  },
  buttonContainer: {
    marginBottom: theme.spacing(2),
  },
}));

function FilterCriteriaFromUpload({ scrollerRef }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const newRecipientList = useSelector((state) => state.newRecipientList);
  const {
    recipientHeaders,
    filterHeaderMapping = {},
    errors,
    headers = [],
    preview = [],
    doneStepIdx,
  } = newRecipientList;
  const invalidFields = Object.keys(errors);

  useEffect(() => {
    // do not re-fetch (and reset) if the user is coming back to this screen from a later step
    if (doneStepIdx < 1) {
      dispatch(getMasterListHeaders());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const masterListHeaders = useMemo(
    () => getSearchableHeaders(recipientHeaders),
    [recipientHeaders]
  );

  const onHeaderToggle = useCallback(
    (header) => {
      dispatch(
        mapFilterCriteriaHeader(
          header,
          filterHeaderMapping[header] === false ? "" : false
        )
      );
    },
    [dispatch, filterHeaderMapping]
  );

  const onExcludeAllHeaders = useCallback(() => {
    for (const header of headers) {
      const headerWasNeverSet = filterHeaderMapping[header] === undefined;
      const headerWasCleared = filterHeaderMapping[header] === "";
      if (headerWasNeverSet || headerWasCleared) {
        dispatch(mapFilterCriteriaHeader(header, false));
      }
    }
  }, [dispatch, filterHeaderMapping, headers]);

  const onHeaderChange = useCallback(
    ({ header, mappedHeader }) =>
      dispatch(mapFilterCriteriaHeader(header, mappedHeader)),
    [dispatch]
  );

  return (
    <Box p={2} className={classes.root} ref={scrollerRef}>
      <Alert
        severity={invalidFields.length ? "error" : "info"}
        variant="outlined"
        className={classes.description}
      >
        {invalidFields.length
          ? t(
              // eslint-disable-next-line max-len
              "Please correct your mapping errors before continuing. The following columns have invalid mappings: {{invalidFields}}.",
              { invalidFields: invalidFields.join(", ") }
            )
          : t(
              // eslint-disable-next-line max-len
              "Map each uploaded column to a Master List field, or exclude it. Included columns must all match on the same row to find a record."
            )}
      </Alert>
      <Grid
        container
        xs={12}
        justify="center"
        className={classes.buttonContainer}
      >
        <Grid item>
          <Box ml={2}>
            <PrimaryButtonContained
              onClick={onExcludeAllHeaders}
              data-test-id="button-exclude-all"
            >
              {t("Exclude All")}
            </PrimaryButtonContained>
          </Box>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        direction="row"
        justify="space-between"
        alignItems="stretch"
      >
        {headers.map((header) => (
          <Grid item key={header} className={classes.item}>
            <CriteriaSelector
              header={header}
              headerMapping={filterHeaderMapping}
              onHeaderChange={onHeaderChange}
              masterListHeaders={masterListHeaders}
              rows={preview}
              onToggle={onHeaderToggle}
              disabled={filterHeaderMapping[header] === false}
              errors={errors}
            />
          </Grid>
        ))}
        {Array(10)
          .fill(null)
          .map((_, idx) => (
            <div key={idx} className={classes.item} />
          ))}
      </Grid>
    </Box>
  );
}

export default memo(FilterCriteriaFromUpload);
